import{pruneLocalizedFields}from'./catalog-localized-fields';
import{CatalogLocalizedFields}from'./CatalogLocalizedFields';
import{declareSourceLocale,removeTranslation,localizationCopy}from'./catalog-localization';
import React from "react";
import { moveRow, type CatalogDraft } from "./catalog-draft";
import type { LaunchLocale } from "../../locales/src/locales";
const labels = {
  en: [
    "Translations",
    "Variants",
    "Media",
    "Categories",
    "Title",
    "Description",
    "Add",
    "Remove",
    "Move up",
    "Move down",
    "Alternative text",
  ],
  es: [
    "Traducciones",
    "Variantes",
    "Medios",
    "Categorías",
    "Título",
    "Descripción",
    "Añadir",
    "Eliminar",
    "Subir",
    "Bajar",
    "Texto alternativo",
  ],
  fr: [
    "Traductions",
    "Variantes",
    "Médias",
    "Catégories",
    "Titre",
    "Description",
    "Ajouter",
    "Supprimer",
    "Monter",
    "Descendre",
    "Texte alternatif",
  ],
  de: [
    "Übersetzungen",
    "Varianten",
    "Medien",
    "Kategorien",
    "Titel",
    "Beschreibung",
    "Hinzufügen",
    "Entfernen",
    "Nach oben",
    "Nach unten",
    "Alternativtext",
  ],
  ja: [
    "翻訳",
    "バリエーション",
    "メディア",
    "カテゴリー",
    "タイトル",
    "説明",
    "追加",
    "削除",
    "上へ",
    "下へ",
    "代替テキスト",
  ],
} satisfies Record<LaunchLocale, string[]>;
export function CatalogEditor({
  value,
  onChange: emitChange,
  locale,
  disabled,
  prefix,
}: {
  value: CatalogDraft;
  onChange: (v: CatalogDraft) => void;
  locale: LaunchLocale;
  disabled: boolean;
  prefix: string;
}) {
  const onChange=(next:CatalogDraft)=>emitChange(pruneLocalizedFields(next));
  const l = labels[locale],languageLabels=localizationCopy(locale);
  const priceLabels={en:["Variant price","Currency code","Amount in minor units","Use the listing base price. Publish with the lowest selectable price."],es:["Precio de variante","Código de moneda","Importe en unidades menores","Usar el precio base. Publicar con el menor precio seleccionable."],fr:["Prix de variante","Code de devise","Montant en unités mineures","Utiliser le prix de base. Publier au plus bas prix sélectionnable."],de:["Variantenpreis","Währungscode","Betrag in Untereinheiten","Basispreis verwenden. Zum niedrigsten wählbaren Preis veröffentlichen."],ja:["バリエーション価格","通貨コード","最小通貨単位の金額","基本価格を使用します。選択可能な最低価格で公開してください。"]}[locale];
  const [optionNames,setOptionNames]=React.useState<Record<string,string>>({});
  const optionLabels={en:['Option name','Add option','Remove option'],es:['Nombre de opción','Añadir opción','Eliminar opción'],fr:['Nom de l’option','Ajouter une option','Supprimer l’option'],de:['Optionsname','Option hinzufügen','Option entfernen'],ja:['オプション名','オプションを追加','オプションを削除']}[locale];
  const validOption=(name:string,row:CatalogDraft['variants'][number])=>!!name.trim()&&name.trim().length<=4000&&!Object.hasOwn(row.options,name.trim())&&!['__proto__','constructor','prototype','actor','role','tenant'].includes(name.trim())&&Object.keys(row.options).length<10;

  function controls<T>(
    rows: T[],
    i: number,
    apply: (rows: T[]) => void,
    minimum = 0,
  ) {
    return (
      <div className="actions">
        <button
          type="button"
          disabled={disabled || i === 0}
          onClick={() => apply(moveRow(rows, i, i - 1))}
        >
          {l[8]}
        </button>
        <button
          type="button"
          disabled={disabled || i === rows.length - 1}
          onClick={() => apply(moveRow(rows, i, i + 1))}
        >
          {l[9]}
        </button>
        <button
          type="button"
          disabled={disabled || rows.length <= minimum}
          onClick={() => apply(rows.filter((_, n) => n !== i))}
        >
          {l[7]}
        </button>
      </div>
    );
  }
  return (
    <fieldset disabled={disabled} className="catalog-editor">
      <legend>
        {l[0]} / {l[1]}
      </legend>
      <section id={prefix + "-translations"}>
        <h3>{l[0]}</h3>
        <label htmlFor={prefix+"-source-locale"}>{languageLabels[0]}</label><select id={prefix+"-source-locale"} value={value.sourceLocale??''} onChange={e=>onChange(declareSourceLocale(value,e.target.value))}><option value="">{languageLabels[1]}</option>{value.translations.map(row=><option key={row.locale} value={row.locale}>{row.locale}</option>)}</select>
        {value.translations.map((row, i) => (
          <fieldset key={row.locale} id={prefix + "-translations-" + i}>
            <legend>{row.locale}</legend>
            <label>
              {l[4]}
              <input
                required
                maxLength={4000}
                value={row.title}
                onChange={(e) =>
                  onChange({
                    ...value,
                    translations: value.translations.map((r, n) =>
                      n === i ? { ...r, title: e.target.value } : r,
                    ),
                  })
                }
              />
            </label>
            <label>
              {l[5]}
              <textarea
                required
                maxLength={4000}
                value={row.description}
                onChange={(e) =>
                  onChange({
                    ...value,
                    translations: value.translations.map((r, n) =>
                      n === i ? { ...r, description: e.target.value } : r,
                    ),
                  })
                }
              />
            </label>
            <CatalogLocalizedFields draft={value} row={row} locale={locale} prefix={prefix+"-translation-"+i} onChange={next=>onChange({...value,translations:value.translations.map((r,n)=>n===i?next:r)})}/>
            <label htmlFor={prefix+"-attribution-"+i}>{languageLabels[2]}</label><select id={prefix+"-attribution-"+i} disabled={!value.sourceLocale||row.locale===value.sourceLocale} value={row.attribution??''} onChange={e=>onChange({...value,translations:value.translations.map((r,n)=>{if(n!==i)return r;const{attribution,...plain}=r;return e.target.value?{...plain,attribution:e.target.value as NonNullable<typeof r.attribution>}:plain})})}><option value="">{languageLabels[1]}</option>{row.locale===value.sourceLocale?<option value="original">{languageLabels[3]}</option>:<><option value="human-translated">{languageLabels[4]}</option><option value="machine-assisted">{languageLabels[5]}</option></>}</select>
            {controls(
              value.translations,
              i,
              (translations) => {
                const removed=value.translations.find(t=>!translations.some(r=>r.locale===t.locale));
                onChange(removed?removeTranslation(value,removed.locale):{...value,translations});
              },
              1,
            )}
          </fieldset>
        ))}
        {(["en", "es", "fr", "de", "ja", "ar"] as const)
          .filter((code) => !value.translations.some((t) => t.locale === code))
          .map((code) => (
            <button
              type="button"
              key={code}
              onClick={() =>
                onChange({
                  ...value,
                  translations: [
                    ...value.translations,
                    { locale: code, title: "", description: "" },
                  ],
                })
              }
            >
              {l[6]} {code}
            </button>
          ))}
      </section>
      <section id={prefix + "-variants"}>
        <h3>{l[1]}</h3>
        {value.variants.map((row, i) => (
          <fieldset key={row.id} id={prefix + "-variants-" + i}>
            <legend>
              {l[1]} {i + 1}
            </legend>
            <label>
              SKU
              <input
                required
                value={row.sku}
                maxLength={4000}
                onChange={(e) =>
                  onChange({
                    ...value,
                    variants: value.variants.map((r, n) =>
                      n === i ? { ...r, sku: e.target.value } : r,
                    ),
                  })
                }
              />
            </label>
            <label><input type="checkbox" checked={Object.hasOwn(row,"price")} onChange={e=>onChange({...value,variants:value.variants.map((v,n)=>{if(n!==i)return v;const {price,...without}=v;return e.target.checked?{...without,price:{currency:"",minor:""}}:without})})}/>{priceLabels[0]}</label>
            {row.price ? <div><label>{priceLabels[1]}<input required maxLength={3} pattern="[A-Z]{3}" value={row.price.currency} onChange={e=>onChange({...value,variants:value.variants.map((v,n)=>n===i?{...v,price:{...row.price!,currency:e.target.value.toUpperCase()}}:v)})}/></label><label>{priceLabels[2]}<input required inputMode="numeric" pattern="[1-9][0-9]*" value={row.price.minor} onChange={e=>onChange({...value,variants:value.variants.map((v,n)=>n===i?{...v,price:{...row.price!,minor:e.target.value}}:v)})}/></label></div>:<p>{priceLabels[3]}</p>}
            {Object.entries(row.options).map(([key, val]) => (
              <div key={key}><label>
                {key}
                <input
                  required
                  value={val}
                  maxLength={4000}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      variants: value.variants.map((r, n) =>
                        n === i
                          ? {
                              ...r,
                              options: { ...r.options, [key]: e.target.value },
                            }
                          : r,
                      ),
                    })
                  }
                />
              </label><button type="button" aria-label={optionLabels[2]+' '+key} onClick={()=>onChange({...value,variants:value.variants.map((r,n)=>n===i?{...r,options:Object.fromEntries(Object.entries(r.options).filter(([k])=>k!==key))}:r)})}>{optionLabels[2]}</button></div>
            ))}
            <label>{optionLabels[0]}<input maxLength={4000} value={optionNames[row.id]??''} onChange={e=>setOptionNames(prev=>({...prev,[row.id]:e.target.value}))}/></label>
            <button type="button" disabled={disabled||!validOption(optionNames[row.id]??'',row)} onClick={()=>{const name=(optionNames[row.id]??'').trim();if(disabled||!validOption(name,row))return;onChange({...value,variants:value.variants.map((r,n)=>n===i?{...r,options:{...r.options,[name]:''}}:r)});setOptionNames(prev=>({...prev,[row.id]:''}))}}>{optionLabels[1]}</button>
            {controls(
              value.variants,
              i,
              (variants) => onChange({ ...value, variants }),
              1,
            )}
          </fieldset>
        ))}
        <button
          type="button"
          disabled={value.variants.length >= 200}
          onClick={() =>
            onChange({
              ...value,
              variants: [
                ...value.variants,
                { id: crypto.randomUUID(), sku: "", options: {} },
              ],
            })
          }
        >
          {l[6]} {l[1]}
        </button>
      </section>
      <section id={prefix + "-media"}>
        <h3>{l[2]}</h3>
        {value.media.map((row, i) => (
          <fieldset key={row.id} id={prefix + "-media-" + i}>
            <legend>
              {l[2]} {i + 1}
            </legend>
            <label>
              {l[10]}
              <input
                required
                maxLength={4000}
                value={row.alt}
                onChange={(e) =>
                  onChange({
                    ...value,
                    media: value.media.map((r, n) =>
                      n === i ? { ...r, alt: e.target.value } : r,
                    ),
                  })
                }
              />
            </label>
            {controls(value.media, i, (media) => onChange({ ...value, media }))}
          </fieldset>
        ))}
      </section>
      <label id={prefix + "-taxonomy"}>
        {l[3]}
        <input
          required
          value={value.taxonomy.join(", ")}
          onChange={(e) =>
            onChange({
              ...value,
              taxonomy: e.target.value.split(",").map((x) => x.trim()),
            })
          }
        />
      </label>
    </fieldset>
  );
}
